import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { useSEO, StructuredData, getServiceSchema, getBreadcrumbSchema } from '../utils/seoHelpers';

const PageContainer = styled.div`
  width: 100%;
  max-width: 100vw;
  position: relative;
  background: black;
  overflow: hidden;
`;

const GradientCircle = styled(motion.div)<{ left: string; top: string }>`
  width: 718px;
  height: 718px;
  position: absolute;
  background: radial-gradient(ellipse 50% 50% at 50% 50%, rgba(255, 67, 163, 0.17) 0%, rgba(255, 67, 163, 0) 100%);
  border-radius: 9999px;
  left: ${props => props.left};
  top: ${props => props.top};
  z-index: 0;
  
  @media (max-width: 1200px) {
    width: 500px;
    height: 500px;
  }
  
  @media (max-width: 768px) {
    width: 300px;
    height: 300px;
  }
`;

const ContentContainer = styled.div`
  position: relative;
  z-index: 1;
  max-width: 100%;
  padding-top: 120px; /* Space for fixed header */
`;

const Section = styled.section`
  padding: 100px 200px;
  position: relative;
  
  @media (max-width: 1200px) {
    padding: 80px 100px;
  }
  
  @media (max-width: 992px) {
    padding: 60px 60px;
  }
  
  @media (max-width: 768px) {
    padding: 50px 20px;
  }
`;

const SectionTitle = styled(motion.h2)`
  color: white;
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 24px;
  background: linear-gradient(90deg, #1F53FF 0%, #FF43A3 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 992px) {
    font-size: 40px;
    margin-bottom: 20px;
  }
  
  @media (max-width: 768px) {
    font-size: 32px;
    margin-bottom: 16px;
  }
`;

const Paragraph = styled(motion.p)`
  color: rgba(255, 255, 255, 0.81);
  font-size: 18px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;
  line-height: 32px;
  margin-bottom: 24px;
  max-width: 800px;
  
  @media (max-width: 992px) {
    font-size: 16px;
    line-height: 28px;
    margin-bottom: 20px;
  }
  
  @media (max-width: 768px) {
    font-size: 15px;
    line-height: 26px;
    margin-bottom: 16px;
  }
`;

const HeroSection = styled.section`
  padding: 100px 200px 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  
  @media (max-width: 1200px) {
    padding: 80px 100px 60px;
  }
  
  @media (max-width: 992px) {
    padding: 60px 60px 40px;
  }
  
  @media (max-width: 768px) {
    padding: 50px 20px 30px;
  }
`;

const HeroTitle = styled(motion.h1)`
  color: white;
  font-size: 64px;
  font-weight: 800;
  margin-bottom: 24px;
  max-width: 900px;
  background: linear-gradient(90deg, #1F53FF 0%, #FF43A3 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 992px) {
    font-size: 48px;
    margin-bottom: 20px;
  }
  
  @media (max-width: 768px) {
    font-size: 36px;
    margin-bottom: 16px;
  }
`;

const HeroSubtitle = styled(motion.p)`
  color: rgba(255, 255, 255, 0.9);
  font-size: 24px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;
  line-height: 36px;
  margin-bottom: 40px;
  max-width: 800px;
  
  @media (max-width: 992px) {
    font-size: 20px;
    line-height: 32px;
    margin-bottom: 30px;
  }
  
  @media (max-width: 768px) {
    font-size: 18px;
    line-height: 28px;
    margin-bottom: 24px;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px;
  margin-top: 60px;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const ServiceCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 40px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-5px);
  }
  
  @media (max-width: 768px) {
    padding: 30px;
  }
`;

const ServiceIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 30px;
  background: linear-gradient(90deg, #1F53FF 0%, #FF43A3 100%);
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
  font-size: 24px;
  color: white;
  
  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    font-size: 20px;
  }
`;

const ServiceTitle = styled.h4`
  color: white;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 12px;
  }
`;

const ServiceDescription = styled.p`
  color: rgba(255, 255, 255, 0.81);
  font-size: 16px;
  line-height: 26px;
  
  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 24px;
  }
`;

const PlatformsSection = styled(Section)`
  background: rgba(255, 255, 255, 0.02);
`;

const PlatformsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  margin-top: 60px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const PlatformCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 30px;
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-5px);
  }
`;

const PlatformIcon = styled.div`
  font-size: 40px;
  margin-bottom: 20px;
`;

const PlatformTitle = styled.h4`
  color: white;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 12px;
`;

const PlatformDescription = styled.p`
  color: rgba(255, 255, 255, 0.81);
  font-size: 14px;
  line-height: 22px;
`;

const ProcessSection = styled(Section)``;

const ProcessSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  margin-top: 60px;
  
  @media (max-width: 992px) {
    gap: 30px;
  }
`;

const ProcessStep = styled(motion.div)`
  display: flex;
  gap: 30px;
  align-items: flex-start;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const StepNumber = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 40px;
  background: linear-gradient(90deg, #1F53FF 0%, #FF43A3 100%);
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 32px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    font-size: 24px;
  }
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h4`
  color: white;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 12px;
  }
`;

const StepDescription = styled.p`
  color: rgba(255, 255, 255, 0.81);
  font-size: 16px;
  line-height: 26px;
  
  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 24px;
  }
`;

const CTASection = styled(Section)`
  text-align: center;
  padding-bottom: 150px;
`;

const CTAButton = styled(motion.button)`
  background: linear-gradient(90deg, #1F53FF 0%, #FF43A3 100%);
  color: white;
  font-size: 18px;
  font-weight: 600;
  padding: 16px 40px;
  border: none;
  border-radius: 30px;
  cursor: pointer;
  margin-top: 40px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(31, 83, 255, 0.3);
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
    padding: 14px 30px;
  }
`;

const SocialMediaMarketingPage: React.FC = () => {
  useSEO({
    title: 'Social Media Marketing Greensboro NC | SMM Services - Market Nest',
    description: 'Professional social media marketing services in Greensboro, NC. Grow your brand on Facebook, Instagram, LinkedIn, and Twitter with strategic social media campaigns.',
    keywords: 'social media marketing Greensboro NC, SMM services, Facebook marketing, Instagram marketing, LinkedIn marketing, social media management',
  });

  const serviceSchema = getServiceSchema(
    'Social Media Marketing',
    'Comprehensive social media marketing services including strategy development, content creation, community management, and paid social advertising across all major platforms.',
    'https://marketnest.org/services/social-media'
  );

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Home', url: 'https://marketnest.org/' },
    { name: 'Services', url: 'https://marketnest.org/#services' },
    { name: 'Social Media Marketing', url: 'https://marketnest.org/services/social-media' }
  ]);
  const socialMediaServices = [
    {
      icon: "📊",
      title: "Social Media Strategy",
      description: "Comprehensive social media strategies tailored to your business goals, target audience, and industry to maximize engagement and ROI."
    },
    {
      icon: "✏️",
      title: "Content Creation & Curation",
      description: "Engaging, on-brand content creation including graphics, videos, and copy that resonates with your audience and drives meaningful interactions."
    },
    {
      icon: "📈",
      title: "Community Management",
      description: "Proactive engagement with your audience, responding to comments and messages, and building a loyal community around your brand."
    },
    {
      icon: "🎯",
      title: "Social Media Advertising",
      description: "Targeted paid social campaigns that reach your ideal customers with precision, driving awareness, leads, and conversions."
    }
  ];
  
  const socialPlatforms = [
    {
      icon: "📘",
      title: "Facebook",
      description: "Build community and drive engagement on the world's largest social network."
    },
    {
      icon: "📸",
      title: "Instagram",
      description: "Showcase your brand visually and connect with audiences through compelling imagery and stories."
    },
    {
      icon: "🐦",
      title: "Twitter",
      description: "Join conversations and establish thought leadership with real-time engagement."
    },
    {
      icon: "💼",
      title: "LinkedIn",
      description: "Generate B2B leads and establish professional authority in your industry."
    },
    {
      icon: "📌",
      title: "Pinterest",
      description: "Drive traffic and sales through inspirational, shoppable content."
    },
    {
      icon: "🎬",
      title: "TikTok",
      description: "Reach younger audiences with creative, authentic short-form video content."
    },
    {
      icon: "🎥",
      title: "YouTube",
      description: "Build brand awareness and educate your audience with in-depth video content."
    },
    {
      icon: "👻",
      title: "Snapchat",
      description: "Connect with Gen Z through ephemeral content and AR experiences."
    }
  ];
  
  const socialMediaProcess = [
    {
      number: 1,
      title: "Social Media Audit & Strategy Development",
      description: "We analyze your current social media presence, competitor strategies, and industry benchmarks to develop a customized social media strategy aligned with your business goals."
    },
    {
      number: 2,
      title: "Content Planning & Calendar Creation",
      description: "We create a comprehensive content calendar that balances promotional content with value-driven posts, ensuring consistent engagement with your audience across all relevant platforms."
    },
    {
      number: 3,
      title: "Content Creation & Curation",
      description: "Our creative team develops high-quality, on-brand content including graphics, videos, and copy that resonates with your target audience and drives meaningful engagement."
    },
    {
      number: 4,
      title: "Community Management & Engagement",
      description: "We actively manage your social media communities, responding to comments and messages, fostering relationships, and building brand loyalty through authentic interactions."
    },
    {
      number: 5,
      title: "Paid Social Advertising",
      description: "We develop and manage targeted paid social campaigns to amplify your reach, drive specific actions, and maximize your advertising ROI across relevant platforms."
    },
    {
      number: 6,
      title: "Analytics & Optimization",
      description: "We continuously monitor performance metrics, provide detailed reports, and make data-driven adjustments to optimize your social media strategy for maximum impact."
    }
  ];
  
  return (
    <PageContainer>
      <StructuredData data={serviceSchema} />
      <StructuredData data={breadcrumbSchema} />
      {/* Background gradient circles */}
      <GradientCircle left="-205px" top="196px" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }} />
      <GradientCircle left="60%" top="439px" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.2 }} />
      <GradientCircle left="70%" top="1388px" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.4 }} />
      <GradientCircle left="-106px" top="2601px" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.6 }} />
      <GradientCircle left="60%" top="3039px" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.8 }} />
      
      <Header />
      <Breadcrumbs items={[
        { label: 'Home', path: '/' },
        { label: 'Services', path: '/#services' },
        { label: 'Social Media Marketing' }
      ]} />
      
      <ContentContainer>
        <HeroSection>
          <HeroTitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Social Media Marketing Services
          </HeroTitle>
          <HeroSubtitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Build brand awareness, engage your audience, and drive conversions with strategic social media marketing
          </HeroSubtitle>
        </HeroSection>
        
        <Section>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Elevate Your Social Media Presence
          </SectionTitle>
          <Paragraph
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            In today's digital landscape, a strong social media presence is essential for building brand awareness, engaging with your audience, and driving business growth. Our comprehensive social media marketing services help you cut through the noise and create meaningful connections with your target audience.
          </Paragraph>
          <Paragraph
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            From strategy development and content creation to community management and paid advertising, our team of social media experts will help you leverage the power of social platforms to achieve your business objectives.
          </Paragraph>
          
          <ServicesGrid>
            {socialMediaServices.map((service, index) => (
              <ServiceCard
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 + index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <ServiceIcon>{service.icon}</ServiceIcon>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
              </ServiceCard>
            ))}
          </ServicesGrid>
        </Section>
        
        <PlatformsSection>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Platforms We Specialize In
          </SectionTitle>
          <Paragraph
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            We help brands establish and grow their presence across all major social media platforms. Our platform-specific strategies ensure you're leveraging the unique features and audience demographics of each channel effectively.
          </Paragraph>
          
          <PlatformsGrid>
            {socialPlatforms.map((platform, index) => (
              <PlatformCard
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 + index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <PlatformIcon>{platform.icon}</PlatformIcon>
                <PlatformTitle>{platform.title}</PlatformTitle>
                <PlatformDescription>{platform.description}</PlatformDescription>
              </PlatformCard>
            ))}
          </PlatformsGrid>
        </PlatformsSection>
        
        <ProcessSection>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Our Social Media Marketing Process
          </SectionTitle>
          <Paragraph
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            We follow a proven methodology to create and execute social media strategies that drive meaningful results for your business. Our systematic approach ensures that every post, campaign, and interaction contributes to your overall business objectives.
          </Paragraph>
          
          <ProcessSteps>
            {socialMediaProcess.map((step, index) => (
              <ProcessStep
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 + index * 0.1 }}
              >
                <StepNumber>{step.number}</StepNumber>
                <StepContent>
                  <StepTitle>{step.title}</StepTitle>
                  <StepDescription>{step.description}</StepDescription>
                </StepContent>
              </ProcessStep>
            ))}
          </ProcessSteps>
        </ProcessSection>
        
        <CTASection>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Ready to Transform Your Social Media Presence?
          </SectionTitle>
          <Paragraph
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Let's discuss how our social media marketing services can help you build brand awareness, engage your audience, and drive business growth.
          </Paragraph>
          <Link to="/contact">
            <CTAButton
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get a Free Social Media Audit
            </CTAButton>
          </Link>
        </CTASection>
        
        <Footer />
      </ContentContainer>
    </PageContainer>
  );
};

export default SocialMediaMarketingPage;
